import Vue from 'vue';
import Router, { Route } from 'vue-router';

// Dynamically import the routes so webpack can chunk the app in smaller pieces
const Tool = () => import('@/views/Tool.vue');
const NotFound = () => import('@/views/NotFound.vue');
const ChangePassword = () => import('@/views/ChangePassword.vue');
const ForgotPassword = () => import('@/views/ForgotPassword.vue');
const Login = () => import('@/views/Login.vue');
const News = () => import('@/views/News.vue');
const Verhogingen = () => import('@/views/Verhogingen.vue');
const NewsItem = () => import('@/views/NewsItem.vue');
const UitzendBevestiging = () => import('@/views/UitzendBevestiging.vue');
const Verify = () => import('@/views/Verify.vue');
const VerifyInvitation = () => import('@/views/VerifyInvitation.vue');
const ResetPassword = () => import('@/views/ResetPassword.vue');
const PrivacyAndCookies = () => import('@/views/PrivacyAndCookies.vue');
const Faq = () => import('@/views/Faq.vue');
const FaqInleners = () => import('@/views/FaqInleners.vue');
const Contact = () => import('@/views/Contact.vue');
const InlenersBeloning = () => import('@/views/InlenersBeloning.vue');
const AbuWijziging = () => import('@/views/AbuWijziging.vue');
const KoppelingAlgemeen = () => import('@/views/KoppelingAlgemeen.vue');
const KoppelingMysolution = () => import('@/views/KoppelingMysolution.vue');
const Application = () => import('@/views/Application.vue');
const TrialPromo = () => import('@/views/TrialPromo.vue');

import store from '@/store/index.ts';
import { vue3AppUrl } from "@/store/adapter";

Vue.use(Router);

const requireLogin = (to: Route, from: Route, next: (to?: string) => void) => {
  if (!store.getters['account/isLoggedIn']) {
    next('login');
  } else {
    next();
  }
};

const requireChangeSubscriptionPermission = (to: Route, from: Route, next: (to?: string) => void) => {
  if (!store.getters['account/isLoggedIn'] || !store.getters['account/userInfo'].permissions.includes('customauth.change_subscription')) {
    next('login');
  } else {
    next();
  }
};


const requirePdfAllowed = (to: Route, from: Route, next: (to?: string) => void) => {
  if (!store.getters['account/isLoggedIn']) {
    next('login');
  } else if (!store.getters['account/pdfAllowed']) {
    next('prices');
  } else {
    next();
  }
};

const router = new Router({
  mode: 'history',
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) { // Scroll to hashed id's when given in route
      return {selector: to.hash};
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes: [
    {
      path: '/',
      name: 'default',
      meta: { isV3: true },
    },
    {
      path: '/application-hr-medewerker',
      name: 'application',
      component: Application,
    },
    {
      path: '/account',
      name: 'account',
      meta: { isV3: true },
    },
    {
      path: '/inleners-list',
      name: 'inleners-list',
      meta: { isV3: true },
    },
    {
      path: '/inleners/:type/:inlener_id',
      name: 'inleners-detail',
      meta: { isV3: true },
    },
    {
      path: '/inleners/:subscriptionId/gelijkwaardige-beloning-detail/:guid',
      name: 'gelijkwaardige-beloning-detail',
      meta: { isV3: true },
    },
    {
      path: '/inleners/:subscription_id/cao-compliance-detail/:id',
      name: 'cao-compliance-detail',
      meta: { isV3: true },
    },
    {
      path: '/account-caos',
      name: 'account-caos',
      meta: { isV3: true , },
    },
    {
      path: '/verhogingen',
      name: 'verhogingen',
      component: Verhogingen,
    },
    {
      path: '/news',
      name: 'news',
      component: News,
    },
    {
      path: '/news-item/:id',
      name: 'news-item',
      component: NewsItem,
    },
    {
      path: '/change-password',
      name: 'change-password',
      component: ChangePassword,
    },
    {
      path: '/caos',
      name: 'caos',
      meta: { isV3: true, },
    },
    {
      path: '/koppelingen/mysolution/caos',
      name: 'koppelingen-mysolution',
      component: KoppelingMysolution,
    },
    {
      path: '/koppelingen/algemeen/caos',
      name: 'koppelingen-algemeen',
      component: KoppelingAlgemeen,
    },
    {
      path: '/kennisbank',
      name: 'kennisbank',
      meta: { isV3: true },
    },
    {
      path: '/contact',
      name: 'contact',
      component: Contact,
    },
    {
      path: '/inleners-beloning',
      name: 'inleners-beloning',
      component: InlenersBeloning,
    },
    {
      path: '/abu-wijziging',
      name: 'abu-wijziging',
      component: AbuWijziging,
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: ForgotPassword,
    },
    {
      path: '/pricing',
      name: 'pricing',
      meta: { isV3: true },
    },
    {
      path: '/login',
      name: 'login',
      meta: { isV3: true },
    },
    {
      path: '/login/:any',
      name: 'microsoft-login',
      component: Login,
      props: { microsoftLogin: true },
    },
    {
      path: '/login/sso/:refresh_token',
      beforeEnter: async (to: Route, from: Route, next: (to?: string) => void) => {
        next('tool');
      },
    },
    {
      path: '/NBBU-leden',
      name: 'register-nbbu',
      meta: { isV3: true },
    },
    {
      path: '/register',
      name: 'register',
      meta: { isV3: true },
    },
    {
      path: '/privacy-and-cookies',
      name: 'privacy-and-cookies',
      component: PrivacyAndCookies,
    },
    {
      path: '/faq-inleners',
      name: 'faq-inleners',
      component: FaqInleners,
    },
    {
      path: '/faq',
      name: 'faq',
      component: Faq,
    },
    {
      path: '/uitzend-bevestiging',
      name: 'uitzend-bevestiging',
      component: UitzendBevestiging,
      beforeEnter: requirePdfAllowed,
    },
    {
      path: '/',
      beforeEnter: (to: Route, from: Route, next: (to?: string) => void) => {
        if (store.getters['account/isLoggedIn']) {
          next('tool');
        } else {
          next('home');
        }
      },
    },
    {
      path: '/trial',
      name: 'trial-promo',
      component: TrialPromo,
    },
    {
      path: '/trial-api',
      name: 'trial-api',
      meta: { isV3: true },
    },
    {
      path: '/trial-supplier',
      name: 'trial-supplier',
      meta: { isV3: true },
    },
    {
      path: '/home',
      name: 'home',
      meta: { isV3: true },
    },
    {
      path: '/tool',
      name: 'tool',
      component: Tool,
    },
    {
      path: '/verify/:uid/:verifyToken',
      name: 'verify',
      component: Verify,
    },
    {
      path: '/verify-invitation/:uid/:verifyToken',
      name: 'verify-invitation',
      component: VerifyInvitation,
    },
    {
      path: '/reset-password/:uid/:resetToken',
      name: 'reset-password',
      component: ResetPassword,
    },
    {
      path: '*',
      name: 'not-found',
      component: NotFound,
    },
    {
      path: '/algemene-voorwaarden',
      name: 'algemene-voorwaarden',
      meta: { isV3: true },
    },
  ],
});

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.isV3)) {
    window.location.href = `${vue3AppUrl}${to.fullPath}`;
  } else {
    next()
  }
})

export default router;
